import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import type { WebMfaFactor } from '@/lib/auth-mfa-gate';

type AuthMfaChallengeProps = {
  factors: WebMfaFactor[];
  loadFailed?: boolean;
  onRetry: () => void;
  onVerified: () => Promise<void>;
};

function verificationMessage(
  error: unknown,
  invalidCode: string,
  rateLimited: string,
  genericError: string,
): string {
  if (!error || typeof error !== 'object') return genericError;
  const candidate = error as { code?: unknown; status?: unknown };
  if (candidate.status === 429 || candidate.code === 'over_request_rate_limit') {
    return rateLimited;
  }
  return candidate.code === 'mfa_verification_failed' ? invalidCode : genericError;
}

export function AuthMfaChallenge({
  factors,
  loadFailed = false,
  onRetry,
  onVerified,
}: AuthMfaChallengeProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [selectedFactorId, setSelectedFactorId] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const factor =
    factors.find((candidate) => candidate.id === selectedFactorId) ?? factors[0] ?? null;

  const verify = async () => {
    if (isVerifying || !factor) return;
    if (!/^\d{6}$/.test(code)) {
      setMessage(t('mfa.validCodeRequired'));
      return;
    }

    setIsVerifying(true);
    setMessage(null);
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        code,
        factorId: factor.id,
      });
      if (error) {
        setMessage(
          verificationMessage(
            error,
            t('mfa.invalidVerificationCode'),
            t('mfa.rateLimited'),
            t('auth.genericError'),
          ),
        );
        return;
      }

      await onVerified();
    } catch {
      setMessage(t('auth.genericError'));
    } finally {
      setIsVerifying(false);
    }
  };

  const signOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    setMessage(null);
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) setMessage(t('auth.genericError'));
    } catch {
      setMessage(t('auth.genericError'));
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="gap-2">
          <p className="text-meta font-semibold text-muted-foreground">{t('mfa.secureSignIn')}</p>
          <CardTitle as="h1">{t('mfa.loginTitle')}</CardTitle>
          <CardDescription>{t('mfa.loginDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {loadFailed || !factor ? (
            <div className="space-y-4" aria-live="assertive">
              <p className="text-body text-destructive">{t('mfa.factorMissing')}</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button type="button" onClick={onRetry} className="flex-1">
                  {t('common.retry')}
                </Button>
                <Button
                  loading={isSigningOut}
                  type="button"
                  variant="outline"
                  onClick={() => void signOut()}
                >
                  {t('profile.logout')}
                </Button>
              </div>
            </div>
          ) : (
            <form
              className="space-y-5"
              onSubmit={(event) => {
                event.preventDefault();
                void verify();
              }}
            >
              <div className="space-y-2">
                {factors.length > 1 ? (
                  <fieldset className="space-y-2">
                    <legend className="text-body font-medium">{t('mfa.chooseFactor')}</legend>
                    <div className="grid gap-2">
                      {factors.map((candidate) => (
                        <label
                          className="flex min-h-11 cursor-pointer items-center gap-3 rounded-control border border-border px-3 py-2 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                          key={candidate.id}
                        >
                          <input
                            checked={candidate.id === factor.id}
                            className="size-4 accent-primary"
                            name="mfa-factor"
                            onChange={() => {
                              setSelectedFactorId(candidate.id);
                              setMessage(null);
                            }}
                            type="radio"
                            value={candidate.id}
                          />
                          <span className="min-w-0 text-body font-medium">
                            {candidate.friendlyName}
                          </span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                ) : null}
                <Label htmlFor="mfa-login-code">{t('mfa.verificationCode')}</Label>
                <InputOTP
                  id="mfa-login-code"
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(value) => {
                    setCode(value.replace(/\D/g, '').slice(0, 6));
                    setMessage(null);
                  }}
                  value={code}
                >
                  <InputOTPGroup>
                    {Array.from({ length: 6 }, (_, index) => (
                      <InputOTPSlot index={index} key={index} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {message ? (
                <p className="text-body text-destructive" role="alert">
                  {message}
                </p>
              ) : null}

              <div className="flex flex-col gap-2">
                <Button disabled={code.length !== 6} loading={isVerifying} type="submit">
                  {isVerifying ? t('mfa.verifying') : t('mfa.continue')}
                </Button>
                <Button
                  loading={isSigningOut}
                  type="button"
                  variant="ghost"
                  onClick={() => void signOut()}
                >
                {t('profile.logout')}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
